import { ReactNode, useEffect, useState } from "react";
import { createRoot, Root } from "react-dom/client";
import cn from "classnames";
import { APP_CONTAINER_CLASSNAME } from "constants/common.constants";
import { AppDOM } from "utils/app.utils";
import s from "./Notification.module.scss";

type NotificationType = "success" | "info" | "warning" | "error" | "open";

type ToastProps = {
  title: string | boolean;
  description: string;
  type: NotificationType;
  onClose: () => void;
  onClick?: () => void;
};

const Toast = ({ title, description, type, onClose, onClick }: ToastProps) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const showId = requestAnimationFrame(() => setVisible(true));
    const hideTimer = window.setTimeout(() => {
      setVisible(false);
      window.setTimeout(onClose, 200);
    }, 4500);
    return () => {
      cancelAnimationFrame(showId);
      window.clearTimeout(hideTimer);
    };
  }, [onClose]);

  return (
    <div
      className={cn(s.toast, s[type], { [s.visible]: visible })}
      role="status"
      onClick={() => {
        onClick?.();
      }}
    >
      <div className={s.body}>
        {title !== false && <p className={s.title}>{title}</p>}
        <p className={s.description}>{description}</p>
      </div>
      <button
        type="button"
        className={s.close}
        onClick={(e) => {
          e.stopPropagation();
          setVisible(false);
          window.setTimeout(onClose, 200);
        }}
      >
        Close
      </button>
    </div>
  );
};

let hostRoot: Root | null = null;
let hostNode: HTMLDivElement | null = null;

const ensureHost = () => {
  const container = AppDOM.querySelector(`.${APP_CONTAINER_CLASSNAME}`) ?? document.body;
  if (!hostNode || !hostNode.isConnected) {
    hostNode = document.createElement("div");
    hostNode.className = s.host;
    container.appendChild(hostNode);
    hostRoot = createRoot(hostNode);
  }
  return hostRoot!;
};

export const Notification = (
  title: string | boolean,
  description: string,
  type: NotificationType,
  action?: () => void
) => {
  const root = ensureHost();

  const close = () => {
    root.render(null as unknown as ReactNode);
  };

  root.render(<Toast title={title} description={description} type={type} onClick={action} onClose={close} />);
};
